
import React from 'react';
import { ArrowRight, Zap, Award, Users, ShieldCheck, IndianRupee, Lightbulb, Droplets } from 'lucide-react';
import { Page } from '../App';
import Logo from '../components/Logo';

interface HomePageProps {
  onNavigate: (page: Page) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  return (
    <div>
      {/* PM Suryaghar Hero Section */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0 opacity-40">
          <img 
            src="https://images.unsplash.com/photo-1613665813446-82a78c44b8fe?auto=format&fit=crop&q=80&w=2000" 
            alt="Residential Solar Rooftop" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-green-950 via-green-900/60 to-transparent z-1"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-white">
          <div className="max-w-4xl">
            {/* Logo + Title Section */}
            <div className="flex flex-wrap items-center gap-6 mb-10">
              <div className="bg-white rounded-2xl p-3 shadow-2xl inline-flex items-center justify-center">
                <Logo className="h-16 sm:h-20 w-auto object-contain" variant="full" />
              </div>
              <div className="flex flex-col">
                <span className="text-3xl sm:text-5xl font-black tracking-tighter">SUN-FARMS</span>
                <span className="text-sm sm:text-lg font-bold text-green-400 uppercase tracking-[0.3em]">Green Energy</span>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-green-300 text-xs font-bold uppercase tracking-widest mb-8">
              <Award size={14} />
              40 Years of Solar Excellence in Bengaluru
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-8">
              PM Surya Ghar <br /> 
              <span className="text-green-400">Muft Bijli Yojana</span>
            </h1>
            <p className="text-xl text-green-50/80 mb-10 leading-relaxed max-w-2xl">
              Sun-farms is your authorized partner for the national rooftop scheme. Empower your home with zero-bill electricity and government-backed savings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-3xl">
                <div className="text-green-400 font-black text-3xl mb-1">₹78,000</div>
                <div className="text-[10px] uppercase font-bold tracking-widest opacity-70">Maximum Subsidy</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-3xl">
                <div className="text-green-400 font-black text-3xl mb-1">120 Months</div>
                <div className="text-[10px] uppercase font-bold tracking-widest opacity-70">Easy Loan Term</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-3xl">
                <div className="text-green-400 font-black text-3xl mb-1">6-7% APR</div>
                <div className="text-[10px] uppercase font-bold tracking-widest opacity-70">Simple Interest</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => onNavigate('contact')}
                className="bg-green-500 hover:bg-green-600 text-white px-10 py-5 rounded-2xl font-black transition-all shadow-2xl flex items-center justify-center gap-3 uppercase tracking-widest text-sm"
              >
                Apply for Subsidy
                <ArrowRight size={20} />
              </button>
              <button 
                onClick={() => onNavigate('pricing')}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-10 py-5 rounded-2xl font-bold transition-all uppercase tracking-widest text-sm"
              >
                View 1kW-10kW Rates
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Legacy Statistics */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex p-3 bg-green-50 text-green-700 rounded-xl mb-6">
                <ShieldCheck size={32} />
              </div>
              <h2 className="text-4xl font-black mb-6">Built on 40 Years of <br /> Trust & Expertise</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Sun-farms (Suryamitra) has been at the forefront of the green revolution in Karnataka for four decades. We don't just sell panels; we build sustainable futures.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
                  <div className="h-12 w-12 bg-green-600 text-white rounded-2xl flex items-center justify-center mb-6">
                    <Users size={24} />
                  </div>
                  <h4 className="text-2xl font-black text-green-800">10,000+</h4>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Youth Trained for Free</p>
                </div>
                <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
                  <div className="h-12 w-12 bg-yellow-500 text-white rounded-2xl flex items-center justify-center mb-6">
                    <IndianRupee size={24} />
                  </div>
                  <h4 className="text-2xl font-black text-slate-900">₹0 Electricity</h4>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">For Muft Bijli Users</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1594818379496-da1e345b0ded?auto=format&fit=crop&q=80&w=1000" 
                className="rounded-[3rem] shadow-2xl" 
                alt="Solar Training" 
              />
              <div className="absolute -bottom-6 -right-6 bg-green-900 text-white p-10 rounded-[2.5rem] shadow-2xl">
                 <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-2">Social Impact</p>
                 <h3 className="text-3xl font-black">Empowering <br /> Unemployed <br /> Youth</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Snapshot */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">Complete Energy Ecosystem</h2>
            <p className="text-slate-500">From domestic rooftops to large-scale high mast lighting.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { 
                icon: <Zap className="text-yellow-600" />, 
                title: 'Rooftop Solar', 
                desc: 'On-grid, Hybrid, and Off-grid systems for all sectors.',
                price: 'Subsidy up to ₹78k',
                img: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&q=80&w=600'
              },
              { 
                icon: <Droplets className="text-blue-600" />, 
                title: 'Irrigation Pumps', 
                desc: 'High-power horticultural pumps for deep borewells.',
                price: '₹50,000 / HP',
                img: 'https://images.unsplash.com/photo-1563514227147-6d2ff665a6a0?auto=format&fit=crop&q=80&w=600'
              },
              { 
                icon: <Lightbulb className="text-orange-600" />, 
                title: 'High Mast Lights', 
                desc: 'Professional lighting for roads, yards, and squares.',
                price: '₹20k - ₹2L per unit',
                img: 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?auto=format&fit=crop&q=80&w=600'
              },
            ].map((service, i) => (
              <div key={i} className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-xl transition-all group">
                <div className="h-64 relative overflow-hidden">
                  <img src={service.img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={service.title} />
                  <div className="absolute top-4 left-4 p-4 bg-white rounded-2xl shadow-xl">{service.icon}</div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                  <p className="text-slate-500 text-sm mb-6 leading-relaxed">{service.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-green-700 font-black text-lg">{service.price}</span>
                    <button onClick={() => onNavigate('products')} className="p-3 bg-slate-50 text-slate-400 rounded-xl hover:bg-green-600 hover:text-white transition-all">
                      <ArrowRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Section */}
      <section className="py-24 bg-green-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-black mb-8">Creating Solar Entrepreneurs</h2>
            <p className="text-xl opacity-80 mb-12 leading-relaxed">
              Our training program for unemployed youth is free of cost, providing the technical and business skills needed to lead Karnataka's green energy future. We are proud to have empowered over 10,000 individuals since our founding.
            </p>
            <div className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 rounded-2xl border border-white/20 font-bold uppercase tracking-widest text-xs">
               Empowered by Bettaswamy Gowda
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
